/**
 * Browser file I/O. Open via <input type="file"> (File System Access API is
 * unavailable on Android Chrome); save via a Blob download; "Share/Send" via the
 * Web Share API (the nicest path on Android — straight to Drive/desktop).
 */
import { readFadein } from "../format/zip";
import { parseDocument } from "../format/parse";
import { DocumentSession } from "./session";

const FADEIN_MIME = "application/octet-stream";

/**
 * Exact-length ArrayBuffer view of bytes, as a Blob/File part. Works around the
 * TS lib's Uint8Array<ArrayBufferLike> vs BlobPart friction; the data here is
 * never SharedArrayBuffer-backed.
 */
function blobPart(bytes: Uint8Array): ArrayBuffer {
  if (bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength) {
    return bytes.buffer as ArrayBuffer;
  }
  return bytes.slice().buffer as ArrayBuffer;
}

/** Parse a picked .fadein file into a fresh session. Throws on invalid files. */
export async function openFadeinFile(file: File): Promise<DocumentSession> {
  const buf = await file.arrayBuffer();
  const xml = readFadein(new Uint8Array(buf));
  const parsed = parseDocument(xml); // throws OSFFormatError / FadeinContainerError
  return new DocumentSession(parsed, file.name);
}

/** Build a session from a stored XML string (recovery / recent reopen). */
export function sessionFromXml(xml: string, filename: string): DocumentSession {
  return new DocumentSession(parseDocument(xml), filename);
}

/** Trigger a download of .fadein bytes with the given filename. */
export function downloadBytes(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([blobPart(bytes)], { type: FADEIN_MIME });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Trigger a download of a UTF-8 text file. */
export function downloadText(text: string, filename: string, mime = "text/plain"): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Replace a .fadein filename's extension (e.g. → .fountain). */
export function withExtension(filename: string, ext: string): string {
  return filename.replace(/\.fadein$/i, "") + ext;
}

/** True if the platform can share files (Android Chrome, iOS Safari). */
export function canShareFiles(bytes: Uint8Array, filename: string): boolean {
  if (typeof navigator === "undefined" || !navigator.canShare) return false;
  try {
    const file = new File([blobPart(bytes)], filename, { type: FADEIN_MIME });
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/** Share the file via the Web Share API. Returns false if unavailable/cancelled. */
export async function shareBytes(
  bytes: Uint8Array,
  filename: string,
): Promise<boolean> {
  if (!navigator.share) return false;
  const file = new File([blobPart(bytes)], filename, { type: FADEIN_MIME });
  try {
    await navigator.share({ files: [file], title: filename });
    return true;
  } catch (err) {
    // AbortError = user cancelled; treat as a non-error no-op.
    if ((err as Error).name === "AbortError") return false;
    throw err;
  }
}
